/**
 * Gemensam formatering för admin/översikt. Alltid sv-SE, oavsett vilket
 * språk besökarens webbläsare är inställd på — annars visar t.ex. en
 * engelskspråkig telefon "1,618 kr" istället för "1 618 kr".
 */

const krFormatter = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });
const numberFormatter = new Intl.NumberFormat('sv-SE');

export const formatKr = (value: number): string => `${krFormatter.format(Math.round(value || 0))} kr`;

export const formatNumber = (value: number): string => numberFormatter.format(value || 0);

export const formatPercent = (ratio: number): string => `${Math.round((ratio || 0) * 100)} %`;

const DAY = 24 * 60 * 60 * 1000;

/** "12 sep" — kort datum utan år (år visas bara om det inte är innevarande). */
export const formatShortDate = (timestamp: number, now: number = Date.now()): string => {
    const date = new Date(timestamp);
    const sameYear = date.getFullYear() === new Date(now).getFullYear();
    return date.toLocaleDateString('sv-SE', sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
};

/** "nyss", "för 3 h sedan", "igår", "för 4 dagar sedan", annars datum. */
export const formatRelativeTime = (timestamp: number, now: number = Date.now()): string => {
    const diff = now - timestamp;
    if (diff < 60 * 1000) return 'nyss';
    if (diff < 60 * 60 * 1000) return `för ${Math.floor(diff / (60 * 1000))} min sedan`;
    if (diff < DAY) return `för ${Math.floor(diff / (60 * 60 * 1000))} h sedan`;
    if (diff < 2 * DAY) return 'igår';
    if (diff < 7 * DAY) return `för ${Math.floor(diff / DAY)} dagar sedan`;
    return formatShortDate(timestamp, now);
};

/** Hela dagar till/från en tidpunkt (positivt = framtid), räknat på kalenderdagar. */
export const daysUntil = (timestamp: number, now: number = Date.now()): number => {
    const startOfDay = (t: number) => {
        const d = new Date(t);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    };
    return Math.round((startOfDay(timestamp) - startOfDay(now)) / DAY);
};

// --- Säljperiodens datum ---
// Datumfälten ("2026-03-03") betyder hela dagar i svensk tid. Förr tolkades de
// som midnatt UTC, så "slutar 3 mars" stängde butiken kl. 01 (02 sommartid)
// samma dag — sista säljdagen försvann. Nu börjar perioden 00:00 och slutar
// 23:59:59 svensk tid, oavsett vilken tidszon webbläsaren står i.

const STOCKHOLM = 'Europe/Stockholm';
const stockholmParts = new Intl.DateTimeFormat('en-US', {
    timeZone: STOCKHOLM, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const stockholmDate = new Intl.DateTimeFormat('sv-SE', { timeZone: STOCKHOLM, year: 'numeric', month: '2-digit', day: '2-digit' });

/** Hur mycket svensk tid ligger före UTC vid en viss tidpunkt (1 h vintertid, 2 h sommartid). */
const stockholmOffset = (utcMs: number): number => {
    const p = Object.fromEntries(stockholmParts.formatToParts(new Date(utcMs)).map(x => [x.type, x.value]));
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return asUtc - Math.floor(utcMs / 1000) * 1000;
};

const stockholmWallTime = (ymd: string, h: number, m: number, s: number, ms: number): number => {
    const [y, mo, d] = ymd.split('-').map(Number);
    const wall = Date.UTC(y, mo - 1, d, h, m, s, ms);
    return wall - stockholmOffset(wall - stockholmOffset(wall));
};

/** "2026-03-03" → 3 mars kl. 00:00 svensk tid. */
export const stockholmDayStart = (ymd: string): number => stockholmWallTime(ymd, 0, 0, 0, 0);

/** "2026-03-03" → 3 mars kl. 23:59:59 svensk tid — sista säljdagen räknas hela dagen. */
export const stockholmDayEnd = (ymd: string): number => stockholmWallTime(ymd, 23, 59, 59, 999);

/** Tidpunkt → "2026-03-03" för ett datumfält, räknat i svensk tid. */
export const toStockholmDateInput = (timestamp: number): string => stockholmDate.format(new Date(timestamp));

/** "Lisa Lärare" → "LL", "Martin" → "M". Används i stället för externa profilbilder. */
export const initialsOf = (name?: string | null): string => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const letters = parts.length === 1 ? parts[0].charAt(0) : parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
    return letters.toLocaleUpperCase('sv-SE');
};
